import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiArrowLeftLight, PiBriefcaseLight, PiCheckCircleLight, PiFilePlusLight, PiSignatureLight, PiFileTextLight, PiCircleNotchLight, PiEnvelopeSimpleLight, PiPaperclipLight, PiMagnifyingGlassLight, PiPaperPlaneRightLight, PiShieldCheckLight, PiTrashLight, PiUserLight } from 'react-icons/pi';
import { candidateAPI, offerAPI } from '../../services/api';
import { OFFER_SALARY_PERIODS, USD_CURRENCY } from '../../utils/compensation';

const ELIGIBLE_STATUSES = ['mutual', 'interview_passed', 'assessment_passed'];
const BLOCKING_OFFER_STATUSES = ['sent', 'viewed', 'negotiation_pending', 'negotiation_countered', 'accepted'];

const salaryPeriods = OFFER_SALARY_PERIODS;

const employmentTypes = [
  { value: 'full_time', label: 'Full time' },
  { value: 'part_time', label: 'Part time' },
  { value: 'contract', label: 'Contract' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'internship', label: 'Internship' },
  { value: 'temporary', label: 'Temporary' }
];

const defaultForm = {
  matchId: '',
  offerTitle: '',
  salaryAmount: '',
  salaryCurrency: 'USD',
  salaryPeriod: 'monthly',
  employmentType: 'full_time',
  startDate: '',
  benefits: '',
  terms: '',
  customMessage: '',
  expiresInDays: 7,
  negotiable: true,
  signatureRequired: false,
  signingInstructions: '',
  offerLetter: null,
  contract: null,
  additionalDocuments: []
};

const inputClass = 'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-400';
const textareaClass = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-6 text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-400';

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatCurrency = (amount, currency = 'USD') => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric) || numeric <= 0) return 'Not set';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(numeric);
};

const candidateName = (match) => {
  const candidate = match?.candidate || {};
  return candidate.fullName || [candidate.firstName, candidate.lastName].filter(Boolean).join(' ') || 'Candidate';
};

const initialsFor = (name) => (
  String(name || 'Candidate')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
);

const scoreTone = (score) => {
  const numeric = Number(score) || 0;
  if (numeric >= 80) return 'border-emerald-100 text-emerald-600';
  if (numeric >= 65) return 'border-sky-100 bg-sky-50 text-sky-700';
  if (numeric >= 50) return 'border-amber-100 text-amber-600';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (ELIGIBLE_STATUSES.includes(normalized)) return 'border-emerald-100 text-emerald-600';
  if (BLOCKING_OFFER_STATUSES.includes(normalized)) return 'border-sky-100 bg-sky-50 text-sky-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const fileLabel = (file) => {
  if (!file) return '';
  const size = file.size ? ` · ${(file.size / 1024 / 1024).toFixed(2)} MB` : '';
  return `${file.name}${size}`;
};

const dedupeMatches = (responses) => {
  const byId = new Map();
  responses.forEach((response) => {
    (Array.isArray(response?.candidates) ? response.candidates : []).forEach((match) => {
      if (match?.matchId && !byId.has(String(match.matchId))) byId.set(String(match.matchId), match);
    });
  });
  return Array.from(byId.values());
};

const salaryFromJob = (match) => {
  const range = match?.job?.salaryRange || {};
  const min = Number(range.min);
  const max = Number(range.max);
  if (min && max) return Math.round((min + max) / 2);
  return min || max || '';
};

const MatchCard = ({ match, selected, disabled, onSelect }) => {
  const name = candidateName(match);
  const candidate = match.candidate || {};
  const contact = candidate.contact || {};
  const score = Math.round(Number(match.overallMatchScore) || 0);
  const latestOfferStatus = match.workflow?.offers?.latestStatus;

  return (
    <button
      type="button"
      onClick={() => !disabled && onSelect(match)}
      disabled={disabled}
      className={`w-full rounded-lg border p-4 text-left transition ${
        selected
          ? 'border-[#0b1f3a] bg-slate-50 shadow-[0_14px_36px_rgba(15,23,42,0.075)]'
          : 'border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.025)] hover:border-sky-200 hover:bg-sky-50/40'
      } disabled:cursor-not-allowed disabled:opacity-60`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-sm font-medium text-sky-700">
          {initialsFor(name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate font-display text-[16px] font-normal text-[#0b1f3a]">{name}</div>
            {selected && <PiCheckCircleLight size={13} className="text-emerald-600" />}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
            {contact.visible && contact.email && <span className="inline-flex items-center gap-1"><PiEnvelopeSimpleLight size={12} /> {contact.email}</span>}
            {candidate.experienceLevel && <span>{labelize(candidate.experienceLevel)}</span>}
            {candidate.experienceYears !== null && candidate.experienceYears !== undefined && <span>{candidate.experienceYears} yrs</span>}
          </div>
        </div>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Role</div>
          <div className="mt-1 truncate text-sm font-medium text-[#0b1f3a]">{match.job?.title || 'Untitled role'}</div>
          <div className="mt-1 text-xs text-slate-500">{match.job?.location || 'No location'} · {labelize(match.job?.jobType)}</div>
        </div>
        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Readiness</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <span className={`rounded-md border px-2 py-0.5 text-xs font-medium ${scoreTone(score)}`}>{score}%</span>
            <span className={`rounded-md border px-2 py-0.5 text-xs font-medium ${statusTone(match.matchStatus)}`}>{labelize(match.matchStatus)}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">{latestOfferStatus ? `Latest offer: ${labelize(latestOfferStatus)}` : 'No prior offer'}</div>
        </div>
      </div>
    </button>
  );
};

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <section className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
    <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
      <div className="flex items-start gap-3">
        <span className="flex items-center justify-center text-sky-600">
          <Icon size={24} />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-[17px] font-normal leading-tight text-[#0b1f3a]">{title}</h2>
          {subtitle && <p className="mt-1 text-xs leading-5 text-slate-500">{subtitle}</p>}
        </div>
      </div>
    </div>
    <div className="p-4 sm:p-5">
      {children}
    </div>
  </section>
);

const Field = ({ label, children, required, hint }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">
      {label}{required && <span className="text-rose-500"> *</span>}
    </span>
    <div className="mt-2">{children}</div>
    {hint && <span className="mt-1 block text-xs leading-5 text-slate-500">{hint}</span>}
  </label>
);

const FileInput = ({ label, file, onChange, onClear, accept = '.pdf,.doc,.docx' }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="text-sm font-medium text-[#0b1f3a]">{label}</div>
        <div className="mt-1 truncate text-xs text-slate-500">{file ? fileLabel(file) : 'PDF, DOC, or DOCX up to backend file limits'}</div>
      </div>
      <div className="flex shrink-0 gap-2">
        {file && (
          <button type="button" onClick={onClear} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-rose-50 hover:text-rose-700" title="Remove file">
            <PiTrashLight size={12} />
          </button>
        )}
        <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
          <PiPaperclipLight size={12} /> Attach
          <input
            type="file"
            className="hidden"
            accept={accept}
            onChange={(event) => onChange(event.target.files?.[0] || null)}
          />
        </label>
      </div>
    </div>
  </div>
);

const ReviewPanel = ({ selectedMatch, form }) => {
  const documents = [
    form.offerLetter ? 'Offer letter' : null,
    form.contract ? 'Contract' : null,
    ...(form.additionalDocuments || []).map((_, index) => `Additional ${index + 1}`)
  ].filter(Boolean);

  return (
    <aside className="space-y-4 lg:sticky lg:top-6">
      <section className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
        <div className="mb-4 flex items-center gap-2">
          <PiSignatureLight size={14} className="text-sky-700" />
          <h2 className="font-display text-[17px] font-normal text-[#0b1f3a]">Offer review</h2>
        </div>
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Candidate</div>
            <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{selectedMatch ? candidateName(selectedMatch) : 'Select a candidate'}</div>
            <div className="mt-1 text-xs text-slate-500">{selectedMatch?.job?.title || 'No role selected'}</div>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Compensation</div>
            <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{formatCurrency(form.salaryAmount, form.salaryCurrency)}</div>
            <div className="mt-1 text-xs text-slate-500">{labelize(form.salaryPeriod)} · {labelize(form.employmentType)}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Expires</div>
              <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{form.expiresInDays} days</div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Signature</div>
              <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{form.signatureRequired ? 'Required' : 'Optional'}</div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Documents</div>
            <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{documents.length ? documents.join(', ') : 'No documents attached'}</div>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
        <div className="mb-2 flex items-center gap-2 font-medium">
          <PiWarningCircleLight size={13} /> Disclaimer
        </div>
        <p>Submitting sends the offer immediately and moves the match to offer sent. Open or accepted offers block duplicate submissions.</p>
      </section>
    </aside>
  );
};

const CreateOfferPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(defaultForm);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const loadMatches = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const responses = await Promise.all(ELIGIBLE_STATUSES.map((matchStatus) => (
        candidateAPI.getEmployerCandidates({
          matchStatus,
          limit: 100,
          offset: 0,
          minScore: 0,
          sortBy: 'updated_at',
          sortOrder: 'DESC'
        })
      )));
      setMatches(dedupeMatches(responses));
    } catch (loadError) {
      setError(extractError(loadError, 'Failed to load offer-ready candidates'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectedMatch = useMemo(() => (
    matches.find((match) => String(match.matchId) === String(form.matchId)) || null
  ), [matches, form.matchId]);

  const selectMatch = useCallback((match) => {
    const salary = salaryFromJob(match);
    setForm((prev) => ({
      ...prev,
      matchId: match.matchId,
      offerTitle: prev.offerTitle || `${match.job?.title || 'Role'} offer`,
      salaryAmount: prev.salaryAmount || salary,
      salaryCurrency: USD_CURRENCY,
      salaryPeriod: match.job?.salaryRange?.period || prev.salaryPeriod
    }));
  }, []);

  useEffect(() => {
    if (!matches.length || form.matchId) return;

    const requestedMatchId = queryParams.get('matchId');
    const requestedJobId = queryParams.get('jobId');
    const requestedCandidateId = queryParams.get('candidateId');

    const match = matches.find((item) => (
      (requestedMatchId && String(item.matchId) === requestedMatchId) ||
      (requestedJobId && requestedCandidateId && String(item.jobId) === requestedJobId && String(item.candidateId) === requestedCandidateId) ||
      (requestedJobId && String(item.jobId) === requestedJobId) ||
      (requestedCandidateId && String(item.candidateId) === requestedCandidateId)
    ));

    if (match) selectMatch(match);
  }, [matches, form.matchId, queryParams, selectMatch]);

  const filteredMatches = useMemo(() => {
    const query = search.trim().toLowerCase();
    return matches.filter((match) => {
      const latestOfferStatus = match.workflow?.offers?.latestStatus;
      const blocked = BLOCKING_OFFER_STATUSES.includes(latestOfferStatus);
      if (!showUnavailable && blocked) return false;
      if (!query) return true;

      return [
        candidateName(match),
        match.candidate?.contact?.email,
        match.job?.title,
        match.job?.location,
        match.matchStatus,
        latestOfferStatus
      ].filter(Boolean).join(' ').toLowerCase().includes(query);
    });
  }, [matches, search, showUnavailable]);

  const counts = useMemo(() => {
    const blocked = matches.filter((match) => BLOCKING_OFFER_STATUSES.includes(match.workflow?.offers?.latestStatus)).length;
    return {
      total: matches.length,
      available: matches.length - blocked,
      blocked
    };
  }, [matches]);

  const validateForm = () => {
    const errors = [];
    if (!selectedMatch) errors.push('Select an eligible candidate match.');
    if (!form.offerTitle.trim()) errors.push('Offer title is required.');
    if (!Number(form.salaryAmount) || Number(form.salaryAmount) <= 0) errors.push('Salary amount must be greater than zero.');
    if (form.salaryCurrency !== USD_CURRENCY) errors.push('Salary currency must be USD.');
    if (!employmentTypes.some((item) => item.value === form.employmentType)) errors.push('Employment type is invalid.');
    if (!salaryPeriods.some((item) => item.value === form.salaryPeriod)) errors.push('Salary period is invalid.');
    if (Number(form.expiresInDays) < 1 || Number(form.expiresInDays) > 60) errors.push('Expiration must be between 1 and 60 days.');
    if (form.customMessage.length > 4000) errors.push('Custom message cannot exceed 4000 characters.');
    if (form.signingInstructions.length > 4000) errors.push('Signing instructions cannot exceed 4000 characters.');
    if (form.benefits.length > 12000) errors.push('Benefits cannot exceed 12000 characters.');
    if (form.terms.length > 12000) errors.push('Terms cannot exceed 12000 characters.');
    return errors;
  };

  const submitOffer = async (event) => {
    event.preventDefault();
    const errors = validateForm();
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }

    const payload = {
      matchId: selectedMatch.matchId,
      offerTitle: form.offerTitle.trim(),
      salaryAmount: Number(form.salaryAmount),
      salaryCurrency: USD_CURRENCY,
      salaryPeriod: form.salaryPeriod,
      employmentType: form.employmentType,
      startDate: form.startDate || undefined,
      benefits: form.benefits.trim(),
      terms: form.terms.trim(),
      customMessage: form.customMessage.trim(),
      expiresInDays: Number(form.expiresInDays),
      negotiable: form.negotiable,
      signatureRequired: form.signatureRequired,
      signingInstructions: form.signingInstructions.trim(),
      offerLetter: form.offerLetter,
      contract: form.contract,
      additionalDocuments: form.additionalDocuments
    };

    setSubmitting(true);
    try {
      if (selectedMatch.jobId && selectedMatch.matchId) {
        await offerAPI.createForMatch(selectedMatch.jobId, selectedMatch.matchId, payload);
      } else {
        await offerAPI.create(payload);
      }
      toast.success('Offer sent');
      navigate('/offers');
    } catch (submitError) {
      toast.error(extractError(submitError, 'Failed to send offer'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-6 font-sans sm:px-6 lg:px-8">
      <form onSubmit={submitOffer} className="mx-auto max-w-[1600px] space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <button
              type="button"
              onClick={() => navigate('/offers')}
              className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,0.035)] transition hover:bg-slate-50"
              title="Back to offers"
            >
              <PiArrowLeftLight size={14} />
            </button>
            <div className="min-w-0">
              <div className="mb-2 inline-flex items-center gap-2 rounded-md border border-sky-100 bg-[#eef9ff] px-2.5 py-1 text-xs font-medium text-sky-700">
                <PiPaperPlaneRightLight size={12} /> New offer
              </div>
              <h1 className="font-display text-2xl font-normal tracking-tight text-[#0b1f3a]">Create Offer</h1>
              <p className="mt-1 text-sm leading-6 text-slate-500">Build and send an offer from a mutual, interview-passed, or assessment-passed match.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setForm(defaultForm)}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white shadow-[0_12px_28px_rgba(11,31,58,0.18)] transition hover:bg-[#12345c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiPaperPlaneRightLight size={13} />}
              Send offer
            </button>
          </div>
        </header>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <main className="space-y-6">
            <Section icon={PiUserLight} title="Candidate Match" subtitle={`${formatNumber(counts.available)} available · ${formatNumber(counts.blocked)} blocked by active or accepted offers`}>
              <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="relative min-w-0 flex-1">
                  <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    placeholder="Search candidates, roles, locations, or statuses"
                  />
                </div>
                <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={showUnavailable}
                    onChange={(event) => setShowUnavailable(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  Show unavailable
                </label>
              </div>

              {error && (
                <div className="mb-4 flex items-start gap-3 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <PiWarningCircleLight className="mt-0.5 shrink-0" size={14} />
                  <span>{error}</span>
                </div>
              )}

              {loading ? (
                <div className="flex min-h-[240px] items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-500">
                  <PiCircleNotchLight className="mr-2 animate-spin" size={14} /> Loading offer-ready candidates
                </div>
              ) : filteredMatches.length === 0 ? (
                <div className="flex min-h-[240px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 text-center">
                  <PiUserLight className="mb-3 text-sky-700" size={19} />
                  <div className="font-display text-lg font-normal text-[#0b1f3a]">No offer-ready matches</div>
                  <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">Offers can only be sent after mutual interest, a passed interview, or a passed assessment.</p>
                </div>
              ) : (
                <div className="grid gap-3 2xl:grid-cols-2">
                  {filteredMatches.map((match) => {
                    const blocked = BLOCKING_OFFER_STATUSES.includes(match.workflow?.offers?.latestStatus);
                    return (
                      <MatchCard
                        key={match.matchId}
                        match={match}
                        selected={String(form.matchId) === String(match.matchId)}
                        disabled={blocked}
                        onSelect={selectMatch}
                      />
                    );
                  })}
                </div>
              )}
            </Section>

            <Section icon={PiBriefcaseLight} title="Role and Compensation" subtitle="Fields in this section map directly to the offer validation contract.">
              <div className="grid gap-4 lg:grid-cols-2">
                <Field label="Offer title" required>
                  <input
                    value={form.offerTitle}
                    onChange={(event) => update('offerTitle', event.target.value)}
                    className={inputClass}
                    maxLength={255}
                    placeholder="Senior Product Engineer offer"
                  />
                </Field>
                <Field label="Employment type" required>
                  <select value={form.employmentType} onChange={(event) => update('employmentType', event.target.value)} className={inputClass}>
                    {employmentTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                  </select>
                </Field>
                <Field label="Salary amount" required>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.salaryAmount}
                    onChange={(event) => update('salaryAmount', event.target.value)}
                    className={inputClass}
                    placeholder="120000"
                  />
                </Field>
                <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3">
                  <Field label="Currency" required>
                    <input
                      value={USD_CURRENCY}
                      readOnly
                      className={`${inputClass} bg-slate-50 uppercase`}
                    />
                  </Field>
                  <Field label="Salary period" required>
                    <select value={form.salaryPeriod} onChange={(event) => update('salaryPeriod', event.target.value)} className={inputClass}>
                      {salaryPeriods.map((period) => <option key={period.value} value={period.value}>{period.label}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Start date">
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(event) => update('startDate', event.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Offer expires in" required hint="The backend accepts 1 to 60 days.">
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={form.expiresInDays}
                    onChange={(event) => update('expiresInDays', event.target.value)}
                    className={inputClass}
                  />
                </Field>
              </div>
            </Section>

            <Section icon={PiFileTextLight} title="Terms, Benefits, and Candidate Message" subtitle="Use concise structured text. JSON is accepted when your offer template needs it.">
              <div className="grid gap-4 lg:grid-cols-2">
                <Field label="Benefits">
                  <textarea
                    value={form.benefits}
                    onChange={(event) => update('benefits', event.target.value)}
                    rows={7}
                    maxLength={12000}
                    className={textareaClass}
                    placeholder="Health cover, retirement contribution, paid leave, remote work support..."
                  />
                </Field>
                <Field label="Terms">
                  <textarea
                    value={form.terms}
                    onChange={(event) => update('terms', event.target.value)}
                    rows={7}
                    maxLength={12000}
                    className={textareaClass}
                    placeholder="Probation period, reporting line, work authorization, confidentiality, background checks..."
                  />
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Custom message">
                  <textarea
                    value={form.customMessage}
                    onChange={(event) => update('customMessage', event.target.value)}
                    rows={5}
                    maxLength={4000}
                    className={textareaClass}
                    placeholder="Add the note the candidate should see when the offer is sent."
                  />
                </Field>
              </div>
            </Section>

            <Section icon={PiShieldCheckLight} title="Negotiation and Signature" subtitle="Control candidate counter-offers and signed offer letter requirements.">
              <div className="grid gap-4 lg:grid-cols-2">
                <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <input
                    type="checkbox"
                    checked={form.negotiable}
                    onChange={(event) => update('negotiable', event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-[#0b1f3a]">Allow negotiation</span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">Candidate can propose salary, start date, benefits, or terms changes.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <input
                    type="checkbox"
                    checked={form.signatureRequired}
                    onChange={(event) => update('signatureRequired', event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-[#0b1f3a]">Require signed offer letter</span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">Acceptance requires a signed document if this is enabled.</span>
                  </span>
                </label>
              </div>
              <div className="mt-4">
                <Field label="Signing instructions">
                  <textarea
                    value={form.signingInstructions}
                    onChange={(event) => update('signingInstructions', event.target.value)}
                    rows={4}
                    maxLength={4000}
                    className={textareaClass}
                    placeholder="Upload the signed offer letter before accepting, or return a signed contract if applicable."
                  />
                </Field>
              </div>
            </Section>

            <Section icon={PiFilePlusLight} title="Documents" subtitle="Attach formal offer files. The backend stores offer letters, contracts, and up to five supporting documents.">
              <div className="grid gap-3 lg:grid-cols-2">
                <FileInput
                  label="Offer letter"
                  file={form.offerLetter}
                  onChange={(file) => update('offerLetter', file)}
                  onClear={() => update('offerLetter', null)}
                />
                <FileInput
                  label="Contract"
                  file={form.contract}
                  onChange={(file) => update('contract', file)}
                  onClear={() => update('contract', null)}
                />
              </div>

              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-medium text-[#0b1f3a]">Additional documents</div>
                    <div className="mt-1 text-xs text-slate-500">{form.additionalDocuments.length} of 5 attached</div>
                  </div>
                  <label className={`inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 ${form.additionalDocuments.length >= 5 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}>
                    <PiPaperclipLight size={12} /> Add files
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={(event) => {
                        const nextFiles = Array.from(event.target.files || []);
                        update('additionalDocuments', [...form.additionalDocuments, ...nextFiles].slice(0, 5));
                        event.target.value = '';
                      }}
                    />
                  </label>
                </div>
                {form.additionalDocuments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {form.additionalDocuments.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2">
                        <div className="min-w-0 truncate text-sm text-slate-700">{fileLabel(file)}</div>
                        <button
                          type="button"
                          onClick={() => update('additionalDocuments', form.additionalDocuments.filter((_, itemIndex) => itemIndex !== index))}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:bg-rose-50 hover:text-rose-700"
                          title="Remove document"
                        >
                          <PiTrashLight size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Section>
          </main>

          <ReviewPanel selectedMatch={selectedMatch} form={form} />
        </div>
      </form>
    </div>
  );
};

export default CreateOfferPage;
